import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader, Box, Text } from "@mantine/core";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/FirebaseCredential";
import ModernMinimalLayout from "./Layouts/ModernMinimalLayout";
import VoiceCentricLayout from "./Layouts/VoiceCentricLayout";
import AssistantStyleLayout from "./Layouts/AssistantStyleLayout";

type Message = {
  id: number;
  sender: "bot" | "user";
  text: string;
  options?: string[];
  listItems?: {
    title: string;
    subtitle: string;
    details: string;
    rawItem: any;
  }[];
};

type Step =
  | "ASK_INTENT"
  | "ASK_CONTACT"
  | "ASK_BRANCH"
  | "VIEW_ITEMS"
  | "ASK_ANYTHING_ELSE"
  | "POST_SELECTION"
  | "FINISHED";

export default function ChatbotWidget() {
  const { gymId } = useParams();
  const [config, setConfig] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [domainError, setDomainError] = useState(false);

  const [messages, setMessages] = useState<Message[]>([
    { id: 1, sender: "bot", text: "Loading..." },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [currentStep, setCurrentStep] = useState<Step>("ASK_INTENT");
  const [userData, setUserData] = useState({
    branchId: "",
    name: "",
    phone: "",
    intent: "",
    selectedItem: "",
  });
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);
  const [branches, setBranches] = useState<{ id: string; name: string }[]>([]);
  const [previousChats, setPreviousChats] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>("current");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Step 1: Fetch the saved config from the backend
  useEffect(() => {
    if (!gymId) return;
    const loadConfig = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const parentDomain = params.get("parentDomain") || window.location.hostname;

        const getData = httpsCallable(functions, "getChatbotAvailableOptions");
        const result = await getData({
          action: "getConfig",
          gymId,
          domain: parentDomain,
        });
        const data = result.data as { config: any };
        if (data?.config) {
          setConfig(data.config);
        } else {
          setConfig({}); // Use empty config (default styles) if none saved
        }
      } catch (error: any) {
        console.error("Error loading chatbot config:", error);
        if (error.code === "permission-denied" || (error.message && error.message.includes("authorized"))) {
          setDomainError(true);
        } else {
          setConfig({});
        }
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, [gymId]);

  // Step 2: After config loads, fetch available options
  useEffect(() => {
    if (!gymId || configLoading) return;

    const checkAvailability = async () => {
      try {
        const getData = httpsCallable(functions, "getChatbotAvailableOptions");
        const result = await getData({ action: "getOptions", gymId });
        const data = result.data as {
          availableOptions: string[];
          branches: { id: string; name: string }[];
        };

        if (data) {
          if (data.availableOptions) setAvailableOptions(data.availableOptions);
          if (data.branches && data.branches.length > 0) {
            setBranches(data.branches);
            setMessages([
              {
                id: Date.now(),
                sender: "bot",
                text:
                  config?.welcomeMessage ||
                  "Hi there! Welcome to our virtual assistant. How can I help you today? Please choose one of the following:",
                options: data.availableOptions,
              },
            ]);
            setCurrentStep("ASK_INTENT");
          } else {
            setMessages([
              {
                id: Date.now(),
                sender: "bot",
                text: "Welcome! We are currently setting up our branches. Please check back later.",
              },
            ]);
          }
        }
      } catch (error) {
        console.error("Error checking available options:", error);
      }
    };

    checkAvailability();
  }, [gymId, configLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addBotMessage = (
    text: string,
    options?: string[],
    listItems?: any[],
  ) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), sender: "bot", text, options, listItems },
    ]);
  };

  const submitEnquiry = (
    service: string,
    itemTitle: string,
    extraNotes?: string,
  ) => {
    let desc = `Website chatbot enquiry. Interested in: ${service} -> ${itemTitle}`;
    if (extraNotes && extraNotes.trim() !== "" && extraNotes !== "Skip") {
      desc += `\nAdditional Notes: ${extraNotes}`;
    }
    desc += `\n\n--- Chat Transcript ---\n`;
    messages.forEach((msg) => {
      const sender = msg.sender === "bot" ? "Bot" : "User";
      desc += `${sender}: ${msg.text}\n`;
    });

    const createEnq = httpsCallable(functions, "submitPublicEnquiry");
    createEnq({
      company_id: gymId,
      branch_id: userData.branchId,
      first_name: userData.name,
      last_name: "",
      mobile_number: userData.phone,
      source: "chatbot",
      added_from: "website_chatbot",
      service: service,
      description: desc,
    }).catch((err) => console.error("Failed to create enquiry", err));
  };

  const handleSend = (text: string = inputValue) => {
    if (!text.trim()) return;
    setMessages((prev) => [...prev, { id: Date.now(), sender: "user", text }]);
    setInputValue("");

    setTimeout(() => {
      switch (currentStep) {
        case "ASK_INTENT":
          setUserData((prev) => ({ ...prev, intent: text }));
          setCurrentStep("ASK_CONTACT");
          addBotMessage(
            "Great choice! Please enter your name and mobile number. (e.g. John, 1234567890)",
          );
          break;

        case "ASK_CONTACT":
          const phoneMatches = text.match(/\d+/g);
          const phone = phoneMatches ? phoneMatches.join("") : "";
          
          if (phone.length !== 10) {
            addBotMessage(
              "Hmm, that doesn't look like a valid phone number. Please enter your name and a valid 10-digit mobile number (e.g. John, 1234567890)."
            );
            return;
          }

          const name =
            text.replace(/\d+/g, "").replace(/,/g, "").trim() || "User";
          setUserData((prev) => ({ ...prev, name, phone }));
          setCurrentStep("ASK_BRANCH");
          addBotMessage(
            `Thanks, ${name}! Please select your preferred branch:`,
            branches.map((b) => b.name),
          );
          const fetchHistory = httpsCallable(
            functions,
            "getChatbotAvailableOptions",
          );
          if (phone) {
            fetchHistory({
              action: "getPreviousChats",
              gymId,
              mobileNumber: phone,
            })
              .then((res: any) => {
                if (res.data?.chats && res.data.chats.length > 0) {
                  setPreviousChats(res.data.chats);
                }
              })
              .catch((err) => console.error("Failed to fetch history", err));
          }
          break;

        case "ASK_BRANCH":
          const selectedBranch = branches.find((b) => b.name === text);
          const bid = selectedBranch?.id || "";
          setUserData((prev) => ({ ...prev, branchId: bid }));
          setCurrentStep("VIEW_ITEMS");
          addBotMessage(`Looking up available ${userData.intent} for you...`);

          const fetchData = httpsCallable(
            functions,
            "getChatbotAvailableOptions",
          );
          fetchData({
            action: "getIntentData",
            gymId,
            branchId: bid,
            intent: userData.intent,
          })
            .then((res: any) => {
              const items = res.data?.items || [];
              if (items.length > 0) {
                setMessages((prev) => {
                  const filtered = prev.filter(
                    (m) =>
                      m.text !==
                      `Looking up available ${userData.intent} for you...`,
                  );
                  return [
                    ...filtered,
                    {
                      id: Date.now() + 1,
                      sender: "bot",
                      text: `Here are the available ${userData.intent}:`,
                      listItems: items,
                    },
                  ];
                });
              } else {
                setMessages((prev) => {
                  const filtered = prev.filter(
                    (m) =>
                      m.text !==
                      `Looking up available ${userData.intent} for you...`,
                  );
                  return [
                    ...filtered,
                    {
                      id: Date.now() + 1,
                      sender: "bot",
                      text: `Sorry, we don't have any ${userData.intent} available at this branch right now.`,
                      options: ["Back to Main Menu", "Exit"],
                    },
                  ];
                });
                setCurrentStep("POST_SELECTION");
              }
            })
            .catch((err) => console.error("Failed to fetch intent data", err));
          break;

        case "ASK_ANYTHING_ELSE":
          submitEnquiry(userData.intent, userData.selectedItem, text);
          setCurrentStep("POST_SELECTION");
          addBotMessage(
            "Thank you for your interest! Our admin will contact you soon.",
          );
          setTimeout(() => {
            addBotMessage("What would you like to do next?", [
              "Back to Main Menu",
              "Exit",
            ]);
          }, 500);
          break;

        case "POST_SELECTION":
          if (text === "Back to Main Menu") {
            setCurrentStep("ASK_INTENT");
            addBotMessage(
              "Please choose one of the following services:",
              availableOptions,
            );
          } else if (text === "Exit") {
            setCurrentStep("FINISHED");
            addBotMessage("Thank you for visiting! Have a great day.");
          }
          break;

        case "FINISHED":
        case "VIEW_ITEMS":
          addBotMessage("I am here to help if you need anything else!");
          break;
      }
    }, 500);
  };

  const handleOptionClick = (option: string) => handleSend(option);

  const handleItemSelect = (item: any) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        sender: "user",
        text: `I am interested in: ${item.title}`,
      },
    ]);
    setTimeout(() => {
      setUserData((prev) => ({ ...prev, selectedItem: item.title }));
      setCurrentStep("ASK_ANYTHING_ELSE");
      addBotMessage(
        "Got it! Is there anything else you would like to add before we send your request?",
        ["Skip"],
      );
    }, 600);
  };

  // Show a loader while fetching config
  if (configLoading) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1a1a2e",
        }}
      >
        <Loader color="blue" size="lg" />
      </Box>
    );
  }

  // Show error if domain is not allowed
  if (domainError) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#1a1a2e",
          color: "white",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <Text color="red" size="xl" weight={700} mb="sm">
          Access Denied
        </Text>
        <Text size="md" color="dimmed">
          This domain is not authorized to load the chatbot.
        </Text>
      </Box>
    );
  }

  // All layout props - same interface as PreviewWidget
  const layoutProps = {
    messages,
    inputValue,
    setInputValue,
    handleSend,
    handleOptionClick,
    handleItemSelect,
    messagesEndRef,
    currentStep,
    config: config || {},
    previousChats,
    activeTab,
    setActiveTab,
  };

  // Render the correct layout based on saved config
  const renderLayout = () => {
    const uiDesign = config?.uiDesign || "modern_minimal";
    switch (uiDesign) {
      case "voice_centric":
        return <VoiceCentricLayout {...layoutProps} />;
      case "assistant_style":
        return <AssistantStyleLayout {...layoutProps} />;
      case "modern_minimal":
      default:
        return <ModernMinimalLayout {...layoutProps} />;
    }
  };

  return (
    <Box style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {renderLayout()}
    </Box>
  );
}
